import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import IndividualConversation from '../../../types/IndividualConversation';
import getDateDifference from '../../../utils/getDateDifference';

ChartJS.register(ArcElement, Tooltip, Legend);

interface ChatAnalysisContainerProps {
  conversation: IndividualConversation;
}

function ChatAnalysisContainer({
  conversation
}: Readonly<ChatAnalysisContainerProps>): React.ReactElement {
  const { messageHistory, conversationSummary } = conversation;

  const healthScore = conversationSummary?.health;
  const totalMessages = messageHistory.length;
  const averageMessageScore =
    totalMessages > 0 && healthScore !== undefined && healthScore !== null
      ? Number((healthScore / totalMessages).toFixed(2))
      : 'Calculating...';

  const duration = useMemo(() => {
    return getDateDifference(
      conversationSummary.firstMessageTime,
      conversationSummary.lastMessageTime
    );
  }, [conversationSummary]);

  interface TagCounts {
    [key: string]: number;
  }

  const chartData = useMemo(() => {
    const tagCounts = messageHistory.reduce<TagCounts>((acc, message) => {
      const responseBlocks =
        message.chatMessageResponse?.chatMessageResponseBlocks;
      const tag =
        responseBlocks && responseBlocks[0] && responseBlocks[0].category
          ? responseBlocks[0].category
          : 'Uncategorised';
      const label = tag.length > 20 ? `${tag.substring(0, 20)}...` : tag;
      acc[label] = (acc[label] || 0) + 1;
      return acc;
    }, {});

    const data = Object.entries(tagCounts)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);

    const top9Data = data.slice(0, 9);
    const otherData = data.slice(9).reduce((acc, { value }) => acc + value, 0);
    if (data.length > 9) {
      top9Data.push({ label: 'Other', value: otherData });
    }
    return top9Data;
  }, [messageHistory]);

  const colors = [
    '#974AD3',
    '#EA5A0A',
    '#EFA00B',
    '#E71111',
    '#049A8F',
    '#41D3BD',
    '#057D99',
    '#663C87',
    '#A76700',
    '#C8CBCB'
  ];

  const doughnutData = {
    labels: chartData.map((item) => item.label),
    datasets: [
      {
        data: chartData.map((item) => item.value),
        backgroundColor: colors,
        borderWidth: 0
      }
    ]
  };

  const doughnutOptions: any = {
    responsive: false,
    maintainAspectRatio: false,
    devicePixelRatio: 1,
    cutout: '80%',
    plugins: {
      legend: {
        display: false
      }
    }
  };

  return (
    <div className='flex flex-wrap gap-4'>
      <div className='border border-black/20 rounded-lg bg-white px-6 py-4 min-w-[280px] flex-1'>
        <h6 className='text-xl font-medium normal-case mb-4'>
          Conversation Summary
        </h6>

        <div className='flex flex-row justify-between'>
          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
              <span className='text-sm text-gray-500'>Total Messages</span>
              <span className='text-xl font-medium'>{totalMessages}</span>
            </div>

            <div className='flex flex-col gap-1'>
              <span className='text-sm text-gray-500'>
                Average Message Score
              </span>
              <span className='text-xl font-medium'>{averageMessageScore}</span>
            </div>
          </div>

          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
              <span className='text-sm text-gray-500'>Health Score</span>
              <span className='text-xl font-medium'>
                {healthScore !== null ? healthScore : 'Calculating...'}
              </span>
            </div>

            <div className='flex flex-col gap-1'>
              <span className='text-sm text-gray-500'>Duration</span>
              <span className='text-xl font-medium'>
                {totalMessages > 1
                  ? `${duration.value} ${duration.timeUnit}`
                  : '0 seconds'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className='border border-black/20 rounded-lg bg-white px-6 py-4 min-w-[280px] flex-1'>
        <h6 className='text-xl font-medium normal-case mb-4'>
          Topic Breakdown
        </h6>

        <div className='flex justify-center'>
          <Doughnut
            data={doughnutData}
            options={doughnutOptions}
            width={130}
            height={130}
          />
        </div>

        <div className='grid grid-cols-2 gap-x-4 gap-y-2 mt-6'>
          {chartData.map((item, idx) => (
            <div key={item.label} className='flex items-center'>
              <div
                className='w-3 h-3 rounded-full mr-2'
                style={{ backgroundColor: colors[idx] || '#000' }}
              />
              <span className='text-sm'>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ChatAnalysisContainer;
